/**
 * iPhone App Store subscriptions (StoreKit 2 via the FrontRowStore plugin).
 *
 * The server verifies every signed transaction with Apple and delivers the
 * membership. A transaction is finished only when the server says so, so an
 * interrupted purchase is redelivered and retried instead of being lost.
 */
import { Capacitor, registerPlugin } from "@capacitor/core";
import axios from "axios";
import { APPLE_PRODUCT_IDS } from "./appleProducts";
import { clearEntitlementCache } from "../components/billing/EntitlementGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Store = registerPlugin("FrontRowStore");
let syncing = null;
let listener = null;

export function isIosApp() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
}

export async function appleAvailable() {
  try {
    return (await axios.get(`${API}/billing/apple/availability`)).data?.available === true;
  } catch {
    return false;
  }
}

export async function loadAppleProducts() {
  const { products = [] } = await Store.products();
  const byId = Object.fromEntries(products.map((p) => [p.id, p]));
  return Object.fromEntries(Object.entries(APPLE_PRODUCT_IDS).map(([plan, id]) => [plan, byId[id] || null]));
}

// Server delivery; finish only after delivery or a permanent refusal.
export async function deliver(transaction) {
  let result;
  try {
    result = (await axios.post(`${API}/billing/apple/activate`, { signed_transaction: transaction.signedTransaction })).data;
  } catch (error) {
    result = error?.response?.data || { finish: false, detail: "network" };
  }
  clearEntitlementCache();
  if (result?.finish) {
    try { await Store.finish({ transactionId: transaction.transactionId }); } catch { /* redelivered later */ }
  }
  return result;
}

/** Returns { outcome: "activated"|"pending"|"cancelled"|"not_activated"|"failed", detail } */
export async function purchasePlan(plan) {
  const productId = APPLE_PRODUCT_IDS[plan];
  if (!productId) return { outcome: "failed", detail: "unknown_plan" };
  const { account_token: accountToken } = (await axios.get(`${API}/billing/apple/account-token`)).data;
  const result = await Store.purchase({ productId, accountToken });
  if (result.status === "pending") return { outcome: "pending" };
  if (result.status === "cancelled") return { outcome: "cancelled" };
  const delivered = await deliver(result);
  if (delivered?.membership_activated) return { outcome: "activated", entitlement: delivered.entitlement };
  return { outcome: delivered?.finish ? "not_activated" : "failed", detail: delivered?.detail };
}

export async function restoreApplePurchases() {
  const { transactions = [] } = await Store.restore();
  const { data } = await axios.post(`${API}/billing/apple/restore`, {
    signed_transactions: transactions.map((t) => t.signedTransaction),
  });
  clearEntitlementCache();
  for (const [index, item] of (data.results || []).entries()) {
    if (item.finish && transactions[index]) {
      try { await Store.finish({ transactionId: transactions[index].transactionId }); } catch { /* ignore */ }
    }
  }
  return data;
}

export async function manageAppleSubscription() {
  await Store.manageSubscriptions();
}

/** Signed-in only: deliver anything StoreKit still holds, then keep listening. */
export async function startAppleTransactionSync(onChange) {
  if (!isIosApp()) return;
  if (!listener) {
    listener = Store.addListener("transactionUpdated", async (transaction) => {
      const result = await deliver(transaction);
      if (onChange) onChange(result);
    });
  }
  if (!syncing) {
    syncing = (async () => {
      if (!(await appleAvailable())) return;
      const { transactions = [] } = await Store.unfinished();
      for (const transaction of transactions) await deliver(transaction);
    })().finally(() => { syncing = null; });
  }
  await syncing;
}

export async function stopAppleTransactionSync() {
  const current = listener;
  listener = null;
  if (current) (await current).remove();
}
